import { ComponentProps, FC, ReactElement, useEffect, useRef, useState } from 'react';

import {
  useCurrentEntertainerQuery,
  useEntertainerReviewsQuery,
} from '@queries/EntertainerQueries';
import { useCurrentUser } from '@queries/UserQueries';

import { useCritiqueDocumentMutation } from '@mutations/CritiqueMutations';
import { useCreateEntertainerReview } from '@mutations/EntertainerMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { useUploadFile, useUploadMultipleFiles } from '@hooks/useUploadFile';

import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';
import { uuid } from '@utilities/uuid';

import Button from '@components/buttons/Button';
import { PostCard, usePostReactions } from '@components/cards/PostCard/PostCard';
import { ImageUploadField } from '@components/form/ImageUploadField';
import {
  MediaItems,
  MediaUploadField,
  useMediaUploadField,
} from '@components/form/MediaUploadField';
import { Textarea } from '@components/form/Textarea';
import { VideoUploadField } from '@components/form/VideoUploadField';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { useDialog } from '@components/layout/Dialog/useDialog';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { Popup } from '@components/layout/Popup/Popup';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';

import { ReceivedBadgesPopup } from '../EntertainerBadgesScreen/ReceivedBadgesPopup';
import { EntertainerReviewCard } from './components/EntertainerReviewCard';

const PostMediaGallery: FC<{
  files: Record<string, { type: 'photo' | 'video'; file?: File }>;
  onChange: (key: string, file: File) => void;
}> = ({ files, onChange }) => {
  const handleChangeImage =
    (key: string, file: File): ComponentProps<typeof ImageUploadField>['onChange'] =>
    (base64Image, blob) => {
      onChange(key, new File([blob], file?.name ?? 'photo.jpg'));
    };

  const handleChangeVideo =
    (key: string, file: File): ComponentProps<typeof VideoUploadField>['onChange'] =>
    (file) => {
      onChange(key, file);
    };

  return (
    <div className="d-grid gap-2">
      {Object.entries(files).map(([key, { type, file }]) => (
        <div key={key}>
          <Card>
            {type === 'photo' && (
              <ImageUploadField placeholder="Add photo" onChange={handleChangeImage(key, file)} />
            )}
            {type === 'video' && (
              <VideoUploadField placeholder="Add video" onChange={handleChangeVideo(key, file)} />
            )}
          </Card>
        </div>
      ))}
    </div>
  );
};

interface EntertainerReviewsProps {
  entertainerId: string;
  entertainerTitle: string;
  referrer?: string;
  isPreviewMode?: boolean;
  headerElement?: ReactElement;
  receivedBadgesPopup?: boolean;
}

export const EntertainerReviewsContent: FC<EntertainerReviewsProps> = ({
  entertainerId,
  entertainerTitle,
  referrer,
  isPreviewMode,
  headerElement,
  receivedBadgesPopup = false,
}) => {
  const { track } = useAnalytics();
  const { createAuthGateHandler } = useAuthGate();
  const { openAlertDialog } = useDialog();
  const { entertainer: currentEntertainer } = useCurrentEntertainerQuery();
  const isCurrentUser = currentEntertainer?._id === entertainerId;
  const { currentUser } = useCurrentUser();

  const { reviewsAreReady, reviews, refetchReviews } = useEntertainerReviewsQuery(entertainerId);
  const { isSubmitting: isSubmittingReview, createEntertainerReviewAsync } =
    useCreateEntertainerReview(entertainerId);

  const [reviewFieldIsVisible, setReviewFieldIsVisible] = useState<boolean>(false);
  const [receivedBadgesPopupIsVisible, setReceivedBadgesPopupIsVisible] =
    useState<boolean>(receivedBadgesPopup);
  const [reviewText, setReviewText] = useState<string>('');
  const meta = referrer ? { referrer } : undefined;
  const [files, setFiles] = useState<Record<string, { type: 'photo' | 'video'; file?: File }>>({});
  const { startUploads } = useUploadMultipleFiles();

  const { selectedFiles, handleClick, handleChangeFile, handleRemoveFile, mediaInputRef } =
    useMediaUploadField();

  const handleAddMedia = (mediaType: 'photo' | 'video') => () => {
    track('Add Post Media', {
      mediaType,
    });

    const key = uuid();

    setFiles((prevFiles) => ({
      ...prevFiles,
      [key]: {
        type: mediaType,
      },
    }));
  };

  // const handleChangeFile = (key: string, file: File) => {
  //   setFiles((prevFiles) => ({
  //     ...prevFiles,
  //     [key]: {
  //       ...prevFiles[key],
  //       file,
  //     },
  //   }));
  // };

  const handleSubmitReview = async () => {
    track('Click Submit Review', {
      entertainerId,
      reviewText,
    });
    createAuthGateHandler(async () => {
      if (!reviewText || isSubmittingReview) {
        return;
      }

      try {
        // const filesToUpload = Object.values(files)
        //   .filter(({ file }) => file)
        //   .map(({ file }) => file);

        const filesToUpload = selectedFiles;

        const uploadedFiles = filesToUpload ? await startUploads(filesToUpload) : [];
        const mediaIds = uploadedFiles?.map(({ mediaId }) => mediaId) ?? [];

        if (filesToUpload.length !== mediaIds.length) {
          throw new Error('Failed to upload all files');
        }

        await createEntertainerReviewAsync({
          text: reviewText,
          mediaIds,
          meta,
        });

        await refetchReviews();

        setReviewText('');
      } catch (error) {
        console.error(error);
      }
    });
  };

  const showPreviewAlert = async () => {
    openAlertDialog({
      title: 'Preview Mode',
      text: 'This feature is disabled in preview mode. Please go to your "werqwith.me" link to view the full experience.',
    });
  };

  return (
    <>
      {headerElement}
      {isCurrentUser && (
        <Button
          roundness="rounded"
          variant="smooth"
          color="neutral"
          onClick={() => setReceivedBadgesPopupIsVisible(true)}
        >
          Received Badges
        </Button>
      )}
      <TitleToolbar
        text="Community Love 💞"
        size="md"
        action={
          <Button
            variant="flat"
            startIcon={<FontAwesomeIcon name="plus" />}
            onClick={() => {
              if (isPreviewMode) {
                showPreviewAlert?.();
                return;
              }

              track('Title Toolbar Action', {
                text: 'Send Love',
              });
              setReviewFieldIsVisible(true);
            }}
          >
            Send Love
          </Button>
        }
      />

      {reviewFieldIsVisible && (
        <div className="mb-4">
          <Card>
            <div className="py-2">
              <Textarea
                placeholder={`LOVE ${entertainerTitle}! Sharing a lil from one of my favorite performances!`}
                onChange={setReviewText}
              />
            </div>
            {selectedFiles.length > 0 && (
              <div className="mb-3">
                <MediaItems
                  className="px-2"
                  selectedFiles={selectedFiles}
                  onRemoveFile={handleRemoveFile}
                />
              </div>
            )}
            <div className="d-flex pb-2 px-2 justify-content-between">
              <div>
                <Button
                  variant="smooth"
                  color="neutral"
                  roundness="rounded"
                  onClick={handleClick}
                  startIcon={<FontAwesomeIcon name="images" />}
                >
                  Add Media
                </Button>
                <div className="d-none">
                  <input
                    ref={mediaInputRef}
                    type="file"
                    name={'review-media'}
                    accept="image/*,video/*"
                    multiple
                    onChange={handleChangeFile}
                  />
                </div>
              </div>
              <div className="ms-auto">
                <Button roundness="rounded" onClick={handleSubmitReview}>
                  Post
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {reviewsAreReady ? (
        <>
          {!!reviews?.length ? (
            <div>
              {reviews.map((review) => (
                <EntertainerReviewCard key={review._id} post={review} />
              ))}
            </div>
          ) : (
            <EmptyState
              title="No love yet"
              text="Be the first to leave some love for this entertainer"
              button={{
                text: 'Send Love',
                onClick: () => {
                  if (isPreviewMode) {
                    showPreviewAlert();
                    return;
                  }

                  track('Empty State Action', {
                    text: 'Send Love',
                  });
                  setReviewFieldIsVisible(true);
                },
              }}
            />
          )}
        </>
      ) : (
        <>
          <Skeleton height={120} />
          <Skeleton height={120} />
          <Skeleton height={120} />
        </>
      )}
      <ReceivedBadgesPopup
        isVisible={receivedBadgesPopupIsVisible}
        onClose={() => setReceivedBadgesPopupIsVisible(false)}
      />
    </>
  );
};

interface EntertainerRewiewsPopupProps extends EntertainerReviewsProps {
  isVisible: boolean;
  onClose: () => void;
}

export const EntertainerRewiewsPopup: FC<EntertainerRewiewsPopupProps> = ({
  isVisible,
  onClose,
  ...sharedProps
}) => {
  return (
    <Popup isVisible={isVisible && !!sharedProps?.entertainerId} onClose={onClose} height="full">
      <Popup.Content>
        {isVisible && sharedProps?.entertainerId && <EntertainerReviewsContent {...sharedProps} />}
      </Popup.Content>
    </Popup>
  );
};
